import React, { useCallback, useMemo, useRef } from 'react';
import * as d3 from 'd3';
import { Download, MapPinned, MousePointer2 } from 'lucide-react';
import { feature, mesh } from 'topojson-client';
import { LineData } from '../core/binning';
import usStatesTopoJson from '../data/us-states-10m.json';
import { useToast } from '../hooks/useToast';
import { Button, Card, CardContent, CardHeader } from './ui';

interface WeatherStationMapProps {
    lines: LineData[];
    selectedLineIds?: number[];
    hoveredLineId?: number | null;
    onHoverLine?: (lineId: number | null) => void;
    height?: number;
}

interface ProjectedStation {
    sid: string;
    name: string;
    state: string;
    latitude: number;
    longitude: number;
    lineId: number;
    x: number;
    y: number;
}

interface StatePathShape {
    id: string | number;
    path: string;
}

const MAP_WIDTH = 400;
const SELECTED_LINE_COLOR = '#ff6b00';
const HOVER_LINE_COLOR = '#ff0000';
const DEFAULT_POINT_COLOR = '#64748b';

const usStatesTopology = usStatesTopoJson as Record<string, any>;
const nationFeature = feature(usStatesTopology as any, usStatesTopology.objects.nation as any);
const stateFeatures = feature(usStatesTopology as any, usStatesTopology.objects.states as any);
const stateBorders = mesh(
    usStatesTopology as any,
    usStatesTopology.objects.states as any,
    (a: any, b: any) => a !== b
);

const WeatherStationMap: React.FC<WeatherStationMapProps> = ({
    lines,
    selectedLineIds = [],
    hoveredLineId = null,
    onHoverLine,
    height = 300,
}) => {
    const toast = useToast();
    const svgRef = useRef<SVGSVGElement>(null);

    const stations = useMemo(() => {
        const uniqueStations = new Map<string, {
            sid: string;
            name: string;
            state: string;
            latitude: number;
            longitude: number;
            lineId: number;
        }>();

        lines.forEach((line, lineId) => {
            if (!line.sid || line.latitude == null || line.longitude == null) {
                return;
            }

            if (!uniqueStations.has(line.sid)) {
                uniqueStations.set(line.sid, {
                    sid: line.sid,
                    name: line.label ?? line.sid,
                    state: line.state ?? '',
                    latitude: line.latitude,
                    longitude: line.longitude,
                    lineId,
                });
            }
        });

        return Array.from(uniqueStations.values());
    }, [lines]);

    const mapGeometry = useMemo(() => {
        const projection = d3.geoAlbersUsa();
        projection.fitExtent([[18, 18], [MAP_WIDTH - 18, height - 18]], nationFeature as any);
        const pathGenerator = d3.geoPath(projection);

        const statePaths: StatePathShape[] = ((stateFeatures as any).features ?? [])
            .map((stateFeature: any, index: number) => ({
                id: stateFeature.id ?? `state-${index}`,
                path: pathGenerator(stateFeature) ?? '',
            }))
            .filter((state: StatePathShape) => state.path.length > 0);

        return {
            projection,
            nationPath: pathGenerator(nationFeature as any) ?? '',
            stateBorderPath: pathGenerator(stateBorders as any) ?? '',
            statePaths,
        };
    }, [height]);

    const projectedStations = useMemo(() => {
        const result: ProjectedStation[] = [];

        stations.forEach((station) => {
            const point = mapGeometry.projection([station.longitude, station.latitude]);
            if (!point) {
                return;
            }

            result.push({
                ...station,
                x: point[0],
                y: point[1],
            });
        });

        return result;
    }, [mapGeometry, stations]);

    const selectedSids = useMemo(() => {
        const sids = new Set<string>();
        selectedLineIds.forEach((lineId) => {
            const sid = lines[lineId]?.sid;
            if (sid) {
                sids.add(sid);
            }
        });
        return sids;
    }, [lines, selectedLineIds]);

    const hoveredSid = hoveredLineId != null ? lines[hoveredLineId]?.sid ?? null : null;
    const hoveredStation = projectedStations.find(station => station.sid === hoveredSid) ?? null;

    const handleDownloadMap = useCallback(() => {
        if (!svgRef.current) {
            toast.error('Station map is not ready');
            return;
        }

        try {
            const clonedSvg = svgRef.current.cloneNode(true) as SVGSVGElement;
            clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
            clonedSvg.setAttribute('width', String(MAP_WIDTH));
            clonedSvg.setAttribute('height', String(height));
            clonedSvg.removeAttribute('class');

            const serializer = new XMLSerializer();
            const svgMarkup = serializer.serializeToString(clonedSvg);
            const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
            link.href = url;
            link.download = `weather-station-map-${timestamp}.svg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.setTimeout(() => URL.revokeObjectURL(url), 0);

            toast.success('Station map saved as SVG');
        } catch (error) {
            console.error('Failed to save station map:', error);
            toast.error('Failed to save station map');
        }
    }, [height, toast]);

    return (
        <Card className="h-full rounded-none border-0 shadow-none" padding="sm">
            <div className="flex items-center justify-between gap-3">
                <CardHeader icon={<MapPinned className="w-4 h-4" />} className="mb-0">
                    Station Map
                </CardHeader>
                <Button
                    variant="secondary"
                    size="sm"
                    icon={<Download className="w-3.5 h-3.5" />}
                    onClick={handleDownloadMap}
                    disabled={projectedStations.length === 0}
                    className="rounded-md border-surface-300 bg-white shadow-none hover:bg-surface-50"
                >
                    Save SVG
                </Button>
            </div>

            <CardContent>
                {projectedStations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center text-surface-500">
                        <MapPinned className="w-8 h-8 mb-3 text-surface-300" />
                        <p className="text-sm font-medium">No station positions available</p>
                        <p className="text-xs mt-1">Load the weather dataset with ACIS station metadata.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2 text-xs text-surface-500">
                            <span className="inline-flex items-center gap-1">
                                <MousePointer2 className="w-3 h-3" />
                                Hover points to focus lines
                            </span>
                        </div>

                        <div className="overflow-hidden border border-surface-300 bg-white">
                            <svg
                                ref={svgRef}
                                viewBox={`0 0 ${MAP_WIDTH} ${height}`}
                                className="w-full"
                                role="img"
                                aria-label="Weather station map"
                            >
                                <rect x="0" y="0" width={MAP_WIDTH} height={height} fill="#ffffff" />

                                {mapGeometry.nationPath && (
                                    <path
                                        d={mapGeometry.nationPath}
                                        fill="#fcfcfd"
                                        stroke="#cbd5e1"
                                        strokeWidth={0.9}
                                    />
                                )}

                                {mapGeometry.statePaths.map((state: StatePathShape) => (
                                    <path
                                        key={state.id}
                                        d={state.path}
                                        fill="#f4f6f8"
                                        stroke="none"
                                    />
                                ))}

                                {mapGeometry.stateBorderPath && (
                                    <path
                                        d={mapGeometry.stateBorderPath}
                                        fill="none"
                                        stroke="#b6c0cc"
                                        strokeWidth={0.6}
                                        strokeOpacity={1}
                                    />
                                )}

                                {projectedStations.map((station) => {
                                    const isHovered = station.sid === hoveredSid;
                                    const isSelected = selectedSids.has(station.sid);
                                    const radius = isHovered ? 5.1 : isSelected ? 3.6 : 2.1;
                                    const opacity = isHovered ? 1 : isSelected ? 0.9 : 0.38;
                                    const fill = isHovered
                                        ? HOVER_LINE_COLOR
                                        : isSelected
                                            ? SELECTED_LINE_COLOR
                                            : DEFAULT_POINT_COLOR;

                                    return (
                                        <g key={station.sid}>
                                            <circle
                                                cx={station.x}
                                                cy={station.y}
                                                r={radius}
                                                fill={fill}
                                                fillOpacity={opacity}
                                                stroke="#ffffff"
                                                strokeWidth={isHovered || isSelected ? 0.9 : 0.55}
                                            />
                                            <circle
                                                cx={station.x}
                                                cy={station.y}
                                                r={Math.max(radius + 5, 8)}
                                                fill="transparent"
                                                className="cursor-pointer"
                                                onMouseEnter={() => onHoverLine?.(station.lineId)}
                                                onMouseLeave={() => onHoverLine?.(null)}
                                            >
                                                <title>{`${station.name} (${station.state})\n${station.sid}`}</title>
                                            </circle>
                                        </g>
                                    );
                                })}
                            </svg>
                        </div>

                        <div className="border border-surface-200 bg-white px-3 py-2 text-xs text-surface-600">
                            {hoveredStation ? (
                                <span>
                                    <span className="font-semibold text-surface-800">{hoveredStation.name}</span>
                                    {` (${hoveredStation.state}) - ${hoveredStation.sid}`}
                                </span>
                            ) : (
                                <span className="inline-flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center gap-1">
                                        <span
                                            className="h-2 w-2 rounded-full"
                                            style={{ backgroundColor: SELECTED_LINE_COLOR }}
                                        />
                                        Selected line
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <span
                                            className="h-2 w-2 rounded-full"
                                            style={{ backgroundColor: HOVER_LINE_COLOR }}
                                        />
                                        Hover focus
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default WeatherStationMap;
